import React, { useState, useEffect } from 'react';
import _merge from 'lodash/merge';

import { getContext, isAnimationDisabled } from 'common';
import Styled from './styles';

export interface LinearProgressIndicatorIndeterminateProps extends React.ComponentPropsWithRef<'div'> {
  /** The thickness of the bar, min is 0.125rem and max is 0.25rem */
  thickness?: 'min' | 'max';
  /** This prop is applied to the slow moving bar */
  slowIndicatorProps?: React.ComponentPropsWithRef<'div'>;
  /** This prop is applied to the fast moving bar */
  fastIndicatorProps?: React.ComponentPropsWithRef<'div'>;
  /** If true, slow down all motion animation for this component.  This property will override any global disableAnimation setting. */
  disableAnimation?: boolean;
  /** If disableAnimation = true, this property sets the progressbar update time (ms). */
  disabledAnimationUpdateTime?: number;
  /** This prop is applied to the wrapper of the indicator when disableAnimation = true. */
  disabledIndicatorWrapperProps?: React.ComponentPropsWithRef<'div'>;
  /** To override the theme please refer to the "Readme" tab to learn more. */
  theme?: any;
}

/** LinearProgressIndicatorIndeterminate, all other props will be provided to the root DOM element */
const LinearProgressIndicatorIndeterminate = React.forwardRef<HTMLElement, LinearProgressIndicatorIndeterminateProps>((props: LinearProgressIndicatorIndeterminateProps, ref) => getContext(({ RTL, theme, disableAnimation }) => {
  const {
    thickness,
    slowIndicatorProps,
    fastIndicatorProps,
    disabledIndicatorWrapperProps,
    disableAnimation: propsdisableAnimation,
    disabledAnimationUpdateTime,
    theme: themeProp,
    ...otherProps
  } = props;
  const [animate, setAnimate] = useState(false);
  const [progress, setProgress] = useState(5);
  const resolvedDisableAnimation = isAnimationDisabled(propsdisableAnimation, disableAnimation);

  useEffect(() => {
    const interval = setInterval(() => {
      if (progress >= 100) {
        setProgress(0);
      } else {
        setProgress(width => width + 20 > 100 ? 100 : width + 20); // mock slowed animation
      }
    }, disabledAnimationUpdateTime);
    return () => clearInterval(interval);
  });


  const resolvedTheme = _merge({}, theme ? theme.LINEAR_PROGRESS_INDETERMINATE : {}, themeProp);
  const animationEnd = () => {
    setAnimate(!animate);
  };
  const { style: slowIndictorPropsStyle, ...otherSlowIndictorProps } = slowIndicatorProps;
  return (
    <Styled.Wrapper
      ref={ref}
      rtl={RTL}
      thickness={thickness}
      $theme={resolvedTheme}
      {...otherProps}
    >
      {resolvedDisableAnimation ?
        <Styled.IndicatorWrapper {...disabledIndicatorWrapperProps} >
          <Styled.Indicator $theme={resolvedTheme} style={{ width: `${progress}%`, ...slowIndictorPropsStyle }} {...otherSlowIndictorProps} />
        </Styled.IndicatorWrapper>
        : !animate ?
          <Styled.SlowIndicator $theme={resolvedTheme} {...slowIndicatorProps} onAnimationEnd={animationEnd} /> :
          <Styled.FastIndicator $theme={resolvedTheme} {...fastIndicatorProps} onAnimationEnd={animationEnd} />
      }
    </Styled.Wrapper>
  );
}));

LinearProgressIndicatorIndeterminate.defaultProps = {
  thickness: 'min',
  slowIndicatorProps: {},
  fastIndicatorProps: {},
  disabledIndicatorWrapperProps: {},
  disableAnimation: undefined,
  disabledAnimationUpdateTime: 3000,
  theme: {}
};

LinearProgressIndicatorIndeterminate.displayName = 'LinearProgressIndicatorIndeterminate';
export default LinearProgressIndicatorIndeterminate;
